import { map } from "nanostores"
import apiRequest from "@utils/apiRequest"
import captureException from "@utils/captureException"

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  verifyPasswordResetCode,
  signInWithPopup,
  GoogleAuthProvider,
  getAuth,
} from "firebase/auth"
import { app } from "../firebase/client"

import type { User } from "firebase/auth"
import type { UserDef } from "@types"
import * as cookies from "tiny-cookie"

interface AuthStoreProps {
  user?: any
  dbUser?: UserDef
  firebaseUser?: User
  token?: string
  isLoading: boolean
  shouldListenForTokenChange: boolean
}

export const $auth = map<AuthStoreProps>({
  isLoading: true,
  shouldListenForTokenChange: true,
  user: undefined,
})

const provider = new GoogleAuthProvider()
export const auth = getAuth(app)

export const fetchUser = async (token: string) => {
  try {
    const res = await apiRequest(`/v1/users/self`, { token })
    $auth.setKey("dbUser", res.data)
    $auth.setKey("isLoading", false)
    const firebaseUser = $auth.get().firebaseUser

    const user = {
      ...res.data,
      email_verified_at: firebaseUser?.emailVerified,
      token: (firebaseUser as any)?.accessToken,
      providerId: firebaseUser?.providerId,
      providerData: firebaseUser?.providerData,
    }
    $auth.setKey("user", user)

    // TODO: enable this when we have a profile page
    // if (!res.data.first_name || !res.data.last_name) {
    //   location.href = "/profile/create"
    // }
  } catch (e: any) {
    console.error(e)
    captureException(e)
  }
}

export const loginWithCredentials = async (email: string, password: string) => {
  await signInWithEmailAndPassword(auth, email, password)
}

export const loginWithPopup = async () => {
  await signInWithPopup(auth, provider)
}

export const logout = async () => {
  await signOut(auth)
  cookies.remove("token")
  $auth.setKey("user", undefined)
  $auth.setKey("dbUser", undefined)
  $auth.setKey("firebaseUser", undefined)
  window.localStorage.removeItem("cartRecipesCount")
  window.localStorage.removeItem("likes:recipes")
  window.localStorage.removeItem("likes:collections")
  window.localStorage.removeItem("likes:bundles")
  window.localStorage.removeItem("favorites:recipes")
  window.localStorage.removeItem("favorites:collections")
  window.localStorage.removeItem("favorites:bundles")
  window.localStorage.removeItem("favorites:defaultFavoriteGroup")
}

export const changePassword = async (newPassword: string) => {
  const user = auth.currentUser
  if (!user) throw new Error("No user found")
  await updatePassword(user, newPassword)
}

export const resetPasswordEmail = async (email: string) => {
  await sendPasswordResetEmail(auth, email)
}

export const passwordResetConfirm = async (code: string, password: string) => {
  await confirmPasswordReset(auth, code, password)
}
export const passwordCodeVerify = async (code: string) => {
  return await verifyPasswordResetCode(auth, code)
}

export const reloadFirebaseUser = async () => {
  const user = auth.currentUser
  if (!user) throw new Error("No user found")
  await user.reload()
  $auth.setKey("firebaseUser", user)
}

export const updateStore = async (user: User | null) => {
  if (user) {
    const token = await user.getIdToken()
    cookies.set("token", token, { path: "/" })
    $auth.setKey("firebaseUser", user)
    await fetchUser(token)
  } else {
    cookies.remove("token")
    $auth.setKey("firebaseUser", undefined)
    $auth.setKey("user", undefined)
    $auth.setKey("dbUser", undefined)
    $auth.setKey("isLoading", false)
  }
}

export const deleteProfile = async () => {
  const user = auth.currentUser
  if (!user) throw new Error("No user found")
  const token = await user.getIdToken()
  await apiRequest("/v1/users/self", {
    method: "DELETE",
    token: token,
  })
}

export const signupWithPopup = async (): Promise<UserDef> => {
  // Disable listening for the token  change here, because otherwise it would
  // fire immediately after signInWithPopup, which in turn would try to fetch
  // the user from the DB, which doesn't exist at this point.
  $auth.setKey("shouldListenForTokenChange", false)
  const response = await signInWithPopup(auth, provider)
  const token = await response.user.getIdToken()
  cookies.set("token", token, { path: "/" })
  const dbResponse = await apiRequest("/v1/users", {
    method: "POST",
    token,
    body: {
      email: response.user.email,
      firebaseId: response.user.uid,
    },
  })
  $auth.setKey("firebaseUser", response.user)
  $auth.setKey("dbUser", dbResponse.user)
  $auth.setKey("shouldListenForTokenChange", true)
  return dbResponse.user
}

export const signupWithCredentials = async (email: string, password: string) => {
  // Disable listening for the token  change here, because otherwise it would
  // fire immediately after createUserWithEmailAndPassword, which in turn
  // would try to fetch the user from the DB, which doesn't exist at this
  // point.
  $auth.setKey("shouldListenForTokenChange", false)
  const response = await createUserWithEmailAndPassword(auth, email, password)
  const token = await response.user.getIdToken()
  cookies.set("token", token, { path: "/" })
  const dbResponse = await apiRequest("/v1/users", {
    method: "POST",
    token,
    body: {
      email: response.user.email,
      firebaseId: response.user.uid,
    },
  })
  $auth.setKey("firebaseUser", response.user)
  $auth.setKey("dbUser", dbResponse.user)
  $auth.setKey("shouldListenForTokenChange", true)
  return dbResponse.user
}
